import { Fragment, useMemo, ReactNode, ReactElement } from 'react';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@restworld/utils-common';

import { IconComponentType } from '../icon-button';
import { Spinner } from '../spinner';
import { Tooltip } from './../tooltip';

const sideMap: Record<string, RadixDropdown.DropdownMenuContentProps['side']> = {
  'top-end': 'top',
  'top-start': 'top',
  'bottom-end': 'bottom',
  'bottom-start': 'bottom',
};

const alignMap: Record<string, RadixDropdown.DropdownMenuContentProps['align']> = {
  'top-end': 'end',
  'top-start': 'start',
  'bottom-end': 'end',
  'bottom-start': 'start',
};

export function Dropdown({
  title,
  triggerElement,
  items,
  groups,
  disabled,
  wrapperStyle,
  placement = 'bottom-start',
}: DropdownProps) {
  const mainItems = useMemo(() => items?.filter((v) => !v.isFooterItem), [items]);
  const footerItems = useMemo(() => items?.filter((v) => v.isFooterItem), [items]);

  return (
    <div className={cn('z-10', wrapperStyle)}>
      <RadixDropdown.Root>
        <RadixDropdown.Trigger asChild disabled={disabled}>
          {/* The wrapper is what Radix turns into the trigger, and Radix does not give a
              non-button child a tab stop: without `tabIndex` the menu cannot be opened with a
              keyboard at all, in any consumer of this component. Keep the trigger element
              itself non-focusable so there is exactly one tab stop. */}
          <div
            tabIndex={disabled ? -1 : 0}
            className="outline-none focus-visible:ring-2 focus-visible:ring-purple-700 rounded-lg"
          >
            {triggerElement}
          </div>
        </RadixDropdown.Trigger>
        <RadixDropdown.Portal>
          {/* `collisionPadding` and the two classes below are what make a long menu usable on a
              phone. Radix measures the space left between the trigger and the viewport edge and
              publishes it as `--radix-dropdown-menu-content-available-height`, but it applies
              nothing on its own: a menu taller than that space is simply painted past the edge,
              and because the element has no scroll container of its own the part below the fold
              cannot be reached at all. Reported 22/09/2026 on the ambassador "Visualizza come"
              menu, which lists a whole challenge cycle plus the Program team and runs off the
              bottom of a phone. Capping the height at the available space and letting the
              content scroll is the fix Radix documents, and it is a no-op on any menu that
              already fits. */}
          <RadixDropdown.Content
            side={sideMap[placement]}
            align={alignMap[placement]}
            sideOffset={8}
            collisionPadding={8}
            className="rounded-lg border border-neutral-90 bg-neutral-10 w-fit p-2 z-[9999] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
            style={{ boxShadow: '0px 4px 16px 0px rgba(0, 0, 26, 0.25)' }}
          >
            {!!title && <span className="!text-base font-medium text-neutral-200 px-5 block mb-2 pt-3">{title}</span>}
            {groups ? (
              <div className="flex w-full flex-col gap-2">
                {groups.map((group, groupIndex) => (
                  <Fragment key={groupIndex}>
                    {groupIndex > 0 && (
                      <RadixDropdown.Separator className="h-px w-full border-0 rounded-full bg-neutral-40 m-0" />
                    )}
                    <div className="flex w-full flex-col">
                      {!!group.title && (
                        <span className="block px-5 pt-3 pb-2 text-base font-medium text-neutral-200">
                          {group.title}
                        </span>
                      )}
                      {group.items.map((item, index) => (
                        <MenuItem key={index} {...item} />
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            ) : (
              <>
                {mainItems?.map((item, index) => {
                  return <MenuItem key={index} {...item} />;
                })}
                {!!footerItems?.length && <RadixDropdown.Separator className="my-2 h-px bg-neutral-90" />}
                {footerItems?.map((item, index) => {
                  return <MenuItem key={index} {...item} />;
                })}
              </>
            )}
          </RadixDropdown.Content>
        </RadixDropdown.Portal>
      </RadixDropdown.Root>
    </div>
  );
}
export default Dropdown;

function MenuItem({
  isCriticalAction,
  Icon,
  label,
  isSelected,
  onClick,
  disabled,
  href,
  isLoading,
  keepOpenOnClick,
  isUnavailable,
  badge,
  disabledTooltip,
}: DropdownItem) {
  const iconClasses = '!h-6 !w-6 min-w-[24px] min-h-[24px]';
  const isDisabled = disabled || isLoading;
  // A disabled item can carry a one-line reason; wrap it in the DS Tooltip only then, so
  // enabled items keep the plain anchor. The dropdown Content sits at z-[9999] and the shared
  // Tooltip defaults to z-[1000], so without an override the tooltip renders *behind* the open
  // menu. Bump only this instance above the menu (cn/tailwind-merge dedupes the z-*), leaving the
  // shared Tooltip's default untouched.
  const withTooltip = (node: ReactElement): ReactElement =>
    isDisabled && disabledTooltip ? (
      <Tooltip content={disabledTooltip} placement="bottom" className="z-[10000]">
        {node}
      </Tooltip>
    ) : (
      node
    );

  return withTooltip(
    <RadixDropdown.Item
      disabled={isDisabled}
      onSelect={(e) => {
        if (keepOpenOnClick) {
          e.preventDefault();
        }
        if (!isDisabled) {
          if (href) {
            window.location.href = href;
          }
          onClick?.(e as unknown as React.SyntheticEvent);
        }
      }}
      className={cn(
        'rounded-lg flex items-center justify-between gap-2 h-12 w-[288px] px-5 font-medium !text-base cursor-pointer outline-none',
        isCriticalAction
          ? 'text-red-700 hover:bg-red-700/10 data-[highlighted]:bg-red-700/20'
          : 'text-neutral-700 hover:bg-neutral-900/10 data-[highlighted]:bg-neutral-900/20',
        isSelected && 'text-purple-700',
        isDisabled && 'text-neutral-100 cursor-not-allowed data-[disabled]:pointer-events-none'
      )}
    >
      <div className="flex items-center gap-2">
        {isLoading ? <Spinner size="xs" color="primary" /> : Icon ? <Icon className={iconClasses} /> : null}
        <span
          className={cn(
            'truncate inline-block',
            (!isSelected && (!!Icon || isLoading)) || (isSelected && !(!!Icon || isLoading))
              ? 'max-w-[225px]'
              : isSelected && (!!Icon || isLoading)
              ? 'max-w-[180px]'
              : 'max-w-[270px]',
            isUnavailable && 'line-through text-neutral-300'
          )}
        >
          {label}
        </span>
      </div>
      {isSelected && <CheckCircleIcon className={`${iconClasses} text-purple-700`} />}
      {badge && !isDisabled && !isSelected && badge}
    </RadixDropdown.Item>
  );
}

type PlacementType = 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start';
export interface DropdownProps {
  placement?: PlacementType;
  title?: string;
  triggerElement: ReactNode;
  items?: DropdownItem[];
  /**
   * Sectioned alternative to `items`. When provided, the menu renders each
   * group with an optional category title and a divider between groups. Takes
   * precedence over `items`.
   */
  groups?: DropdownGroup[];
  disabled?: boolean;
  /** Additional class names applied to the wrapper div */
  wrapperStyle?: string;
}
export interface DropdownGroup {
  /** Optional category header shown above the group's items. */
  title?: string;
  items: DropdownItem[];
}
export interface DropdownItem {
  label: string;
  isCriticalAction?: boolean;
  Icon?: IconComponentType;
  isSelected?: boolean;
  isFooterItem?: boolean;
  onClick?: (e?: React.SyntheticEvent) => void;
  disabled?: boolean;
  href?: string;
  isLoading?: boolean;
  keepOpenOnClick?: boolean;
  /** Renders the label with strikethrough + muted style while keeping the item clickable */
  isUnavailable?: boolean;
  /** Right-aligned trailing element (e.g. a DS Badge such as "Beta"); hidden while disabled */
  badge?: ReactNode;
  /** Explains why the item is disabled; shown as a tooltip only while `disabled` */
  disabledTooltip?: string;
}
